import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FaCheckCircle, FaSpinner, FaDownload, FaStar, FaWhatsapp, FaArrowRight, FaDumbbell, FaPlay, FaBook, FaClipboardList, FaTimesCircle, FaClock, FaShieldAlt } from 'react-icons/fa';
import PersonalizedPlanForm from '@/components/PersonalizedPlanForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONALIZED_PLANS = ['premium', 'gold', 'competitivo', 'competitive', 'vip'];

const PRODUCT_TYPES = {
  subscription: { icon: FaDumbbell, text: 'Accede a tu Plan', route: '/dashboard' },
  ebook: { icon: FaBook, text: 'Accede a tu Ebook', route: '/ebooks' },
  video_membership: { icon: FaPlay, text: 'Accede a tu Contenido Exclusivo', route: '/videos' },
  recipe_videos: { icon: FaPlay, text: 'Accede a tus Recetarios en Video', route: '/videos' },
  default: { icon: FaStar, text: 'Accede a tu Contenido', route: '/dashboard' }
};

const WHATSAPP_NUMBER = '573195825712';

const POLLING_INTERVAL_MS = 3000;
const MAX_POLLING_DURATION_MS = 5 * 60 * 1000; // 5 minutos

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const [pageStatus, setPageStatus] = useState('checking'); // checking, approved, pending, rejected
  const [purchaseData, setPurchaseData] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [canRetry, setCanRetry] = useState(false);
  
  const [showPersonalizedForm, setShowPersonalizedForm] = useState(false);
  const [formCompleted, setFormCompleted] = useState(false);
  const [needsForm, setNeedsForm] = useState(false);
  const [isRenewal, setIsRenewal] = useState(false);
  
  const [pollCount, setPollCount] = useState(0);
  const [pollingActive, setPollingActive] = useState(false);
  
  const pollingRef = useRef(null);
  const startTimeRef = useRef(null);
  const externalRefRef = useRef(null);

  // Extraer external_reference de URL params
  const getExternalReference = useCallback(() => {
    return searchParams.get('external_reference') || externalRefRef.current || null;
  }, [searchParams]);

  // Detener polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setPollingActive(false);
  }, []);

  // Procesar respuesta de approved
  const handleApproved = useCallback((data) => {
    stopPolling();
    setPageStatus('approved');
    setPurchaseData(data);
    
    // Limpiar localStorage de pago pendiente
    localStorage.removeItem('deisycfit_pending_preference_id');
    localStorage.removeItem('deisycfit_pending_payment_email');
    localStorage.removeItem('deisycfit_pending_payment_time');
    
    // Auto-login
    if (data.token && data.user) {
      localStorage.setItem('deisycfit_token', data.token);
      localStorage.setItem('deisycfit_email', data.user.email);
      localStorage.setItem('deisycfit_user', JSON.stringify(data.user));
      localStorage.setItem('deisycfit_logged_in', 'true');
    } else {
      const savedUser = JSON.parse(localStorage.getItem('deisycfit_user') || '{}');
      if (savedUser.email) {
        savedUser.subscription_plan = data.plan_type || savedUser.subscription_plan;
        localStorage.setItem('deisycfit_user', JSON.stringify(savedUser));
        localStorage.setItem('deisycfit_logged_in', 'true');
      }
    }
    
    // Verificar si necesita formulario
    const planType = data.plan_type;
    if (planType && PERSONALIZED_PLANS.includes(planType.toLowerCase())) {
      setNeedsForm(true);
    }
    
    toast.success('Pago verificado exitosamente!');
  }, [stopPolling]);

  // Consultar estado real del pago - soporta external_reference, collection_id, preference_id, buyer_email
  const checkStatus = useCallback(async (extRef, altParams = {}) => {
    if (!extRef && !altParams.collection_id && !altParams.preference_id && !altParams.buyer_email) return null;
    
    try {
      const payload = {};
      if (extRef) payload.external_reference = extRef;
      if (altParams.collection_id) payload.collection_id = altParams.collection_id;
      if (altParams.preference_id) payload.preference_id = altParams.preference_id;
      if (altParams.buyer_email) payload.buyer_email = altParams.buyer_email;
      
      const response = await axios.post(`${API}/orders/check-status`, payload, { timeout: 15000 });
      
      const result = response.data;
      console.log('check-status response:', result);
      
      if (!result.success && result.status === 'not_found') {
        return 'not_found';
      }
      
      if (result.status === 'approved' && result.success) {
        handleApproved(result);
        return 'approved';
      }
      
      if (result.status === 'pending') {
        setStatusMessage(result.message || 'Tu pago esta siendo procesado...');
        return 'pending';
      }
      
      if (['rejected', 'cancelled', 'failed', 'refunded'].includes(result.status)) {
        stopPolling();
        setPageStatus('rejected');
        setStatusMessage(result.message || 'El pago no fue aprobado.');
        setCanRetry(result.can_retry || false);
        return 'rejected';
      }
      
      return 'pending';
    } catch (err) {
      console.error('Error en check-status:', err);
      return 'error';
    }
  }, [handleApproved, stopPolling]);

  // Iniciar polling
  const startPolling = useCallback((extRef) => {
    if (pollingRef.current) return;
    
    startTimeRef.current = Date.now();
    setPollingActive(true);
    
    // Parse alt params for polling
    const isAlt = extRef && extRef.startsWith('alt:');
    const altId = isAlt ? extRef.replace('alt:', '') : null;
    const altParams = altId ? { collection_id: altId } : {};
    const realRef = isAlt ? null : extRef;
    
    pollingRef.current = setInterval(async () => {
      const elapsed = Date.now() - startTimeRef.current;
      
      // Verificar timeout de 5 minutos
      if (elapsed >= MAX_POLLING_DURATION_MS) {
        stopPolling();
        setStatusMessage('La verificacion esta tomando mas tiempo del esperado. Si ya realizaste el pago, el acceso se activara automaticamente cuando sea confirmado. Tambien puedes escribirnos por WhatsApp.');
        return;
      }
      
      setPollCount(prev => prev + 1);
      const result = await checkStatus(realRef, altParams);
      
      if (result === 'approved' || result === 'rejected') {
        return;
      }
    }, POLLING_INTERVAL_MS);
  }, [checkStatus, stopPolling]);

  // Efecto principal - ejecuta al montar
  useEffect(() => {
    const savedUser = localStorage.getItem('deisycfit_user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        if (userData.subscription_plan && userData.subscription_plan !== 'free') {
          setIsRenewal(true);
        }
      } catch (e) {}
    }
    
    const extRef = searchParams.get('external_reference');
    const collectionId = searchParams.get('collection_id');
    const preferenceId = searchParams.get('preference_id');
    const paymentId = searchParams.get('payment_id');
    externalRefRef.current = extRef;
    
    // Construir params alternativos para búsqueda robusta
    const altParams = {};
    if (collectionId) altParams.collection_id = collectionId;
    if (preferenceId) altParams.preference_id = preferenceId;
    if (paymentId) altParams.collection_id = paymentId;
    
    // FALLBACK: usar localStorage si no hay params en URL
    if (!extRef && !collectionId && !preferenceId) {
      const savedPrefId = localStorage.getItem('deisycfit_pending_preference_id');
      const savedEmail = localStorage.getItem('deisycfit_pending_payment_email');
      const savedTime = localStorage.getItem('deisycfit_pending_payment_time');
      
      // Solo usar si el pago fue iniciado en los últimos 30 minutos
      const isRecent = savedTime && (Date.now() - parseInt(savedTime)) < 30 * 60 * 1000;
      
      if (isRecent && savedPrefId) {
        altParams.preference_id = savedPrefId;
      }
      if (isRecent && savedEmail) {
        altParams.buyer_email = savedEmail;
      }
    }
    
    // Determinar contexto de la URL para fallback
    const urlCollectionStatus = searchParams.get('collection_status') || searchParams.get('status');
    const isErrorRoute = location.pathname.includes('/pago/error') || location.pathname.includes('cancel');
    const isPendingRoute = location.pathname.includes('/pago/pendiente');
    
    // Si viene de /pago/error o cancel sin referencia
    // IMPORTANTE: incluir paymentId y altParams.collection_id para cuando MP solo envía payment_id
    const hasAnyIdentifier = extRef || collectionId || preferenceId || paymentId || altParams.preference_id || altParams.buyer_email || altParams.collection_id;
    
    if (!hasAnyIdentifier) {
      if (isErrorRoute || location.pathname.includes('cancel')) {
        setPageStatus('rejected');
        setStatusMessage('El pago fue cancelado o rechazado.');
        setCanRetry(true);
        return;
      }
      
      // ÚLTIMO RECURSO: buscar pago reciente por email del usuario
      const userEmail = localStorage.getItem('deisycfit_email') || 
        (() => { try { return JSON.parse(localStorage.getItem('deisycfit_user') || '{}').email; } catch(e) { return null; } })();
      
      if (userEmail) {
        const emailAltParams = { buyer_email: userEmail };
        const initEmailCheck = async () => {
          const result = await checkStatus(null, emailAltParams);
          if (result === 'approved') return;
          if (result === 'rejected') return;
          // Si no encontró nada, mostrar mensaje de espera
          setPageStatus('pending');
          setStatusMessage('No pudimos identificar tu pago. Si ya realizaste el pago, el acceso se activara automaticamente. Escribenos por WhatsApp si necesitas ayuda.');
        };
        initEmailCheck();
        return;
      }
      
      // Sin ningún identificador → no podemos hacer polling
      setPageStatus('pending');
      setStatusMessage('No pudimos identificar tu pago. Si ya realizaste el pago, el acceso se activara automaticamente. Escribenos por WhatsApp si necesitas ayuda.');
      return;
    }
    
    // Consulta inicial - usar external_reference como primario, altParams como fallback
    const initCheck = async () => {
      const result = await checkStatus(extRef, altParams);
      
      if (result === 'approved') {
        return;
      }
      
      if (result === 'rejected') {
        return;
      }
      
      if (result === 'not_found') {
        if (isErrorRoute || urlCollectionStatus === 'rejected' || urlCollectionStatus === 'cancelled') {
          setPageStatus('rejected');
          setStatusMessage('El pago fue rechazado o cancelado por MercadoPago.');
          setCanRetry(true);
          return;
        }
        // Polling con lo que tengamos
        setPageStatus('pending');
        setStatusMessage('Verificando tu pago...');
        startPolling(extRef || `alt:${collectionId || paymentId || preferenceId}`);
        return;
      }
      
      // Pending o error de red → iniciar polling
      setPageStatus('pending');
      if (isPendingRoute) {
        setStatusMessage('Tu pago esta siendo procesado por tu banco. Por favor espera...');
      }
      startPolling(extRef || `alt:${collectionId || paymentId || preferenceId}`);
    };
    
    initCheck();
    
    return () => {
      stopPolling();
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleFormComplete = () => {
    setFormCompleted(true);
    setShowPersonalizedForm(false);
    toast.success('Formulario completado! Tu plan personalizado estara listo en 48 horas.');
  };

  const handleGoToDashboard = () => {
    const productType = purchaseData?.product_type || 'subscription';
    const route = PRODUCT_TYPES[productType]?.route || '/dashboard';
    const isLoggedIn = localStorage.getItem('deisycfit_logged_in');
    const hasUser = localStorage.getItem('deisycfit_user');
    
    if (isLoggedIn === 'true' && hasUser) {
      window.location.href = route;
    } else {
      toast.info('Por favor inicia sesion para acceder a tu cuenta');
      window.location.href = `/login?redirect=${encodeURIComponent(route)}`;
    }
  };

  const openWhatsAppSupport = () => {
    const reference = externalRefRef.current || searchParams.get('external_reference') || '';
    const message = encodeURIComponent(`Hola! Acabo de realizar un pago en DeisyCFit pero no se ha acreditado. Mi referencia es: ${reference}`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const getProductConfig = () => {
    const productType = purchaseData?.product_type || 'subscription';
    return PRODUCT_TYPES[productType] || PRODUCT_TYPES.default;
  };

  // ========== RENDER: CHECKING (carga inicial) ==========
  if (pageStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
          data-testid="payment-checking"
        >
          <FaSpinner className="animate-spin text-6xl mx-auto mb-4" style={{ color: 'var(--brand-primary)' }} />
          <h2 className="text-2xl font-bold text-white">Verificando tu pago...</h2>
          <p className="text-gray-400 mt-2">Consultando estado en MercadoPago</p>
        </motion.div>
      </div>
    );
  }

  // ========== RENDER: PENDING (procesando con polling) ==========
  if (pageStatus === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0a0a0a' }}>
        <BackgroundEffects />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full relative z-10"
          data-testid="payment-pending"
        >
          <div 
            className="rounded-3xl p-8 text-center"
            style={{ 
              background: 'linear-gradient(145deg, rgba(50, 50, 50, 0.9), rgba(30, 30, 30, 0.95))',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
            }}
          >
            {/* Icono animado de procesamiento */}
            <div className="relative w-24 h-24 mx-auto mb-6">
              <motion.div 
                className="w-24 h-24 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FFC107, #FF9800)' }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <FaShieldAlt className="text-white text-4xl" />
              </motion.div>
              {pollingActive && (
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{ border: '3px solid #FFC107' }}
                  animate={{ scale: [1, 1.4, 1], opacity: [0.8, 0, 0.8] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              )}
            </div>

            <h1 
              className="text-3xl font-bold mb-3" 
              style={{ fontFamily: 'Oswald, sans-serif', color: '#FFC107' }}
              data-testid="pending-title"
            >
              PROCESANDO TU PAGO
            </h1>
            
            <p className="text-gray-300 mb-2 text-lg" data-testid="pending-message">
              {statusMessage || 'Tu pago esta siendo procesado...'}
            </p>

            <p className="text-gray-400 text-sm mb-6">
              No cierres esta ventana. Te daremos acceso automaticamente cuando sea aprobado.
            </p>

            {/* Indicador de polling activo */}
            {pollingActive && (
              <motion.div 
                className="flex items-center justify-center gap-2 mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="flex gap-1">
                  <motion.div className="w-2 h-2 rounded-full bg-yellow-400" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0 }} />
                  <motion.div className="w-2 h-2 rounded-full bg-yellow-400" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }} />
                  <motion.div className="w-2 h-2 rounded-full bg-yellow-400" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }} />
                </div>
                <span className="text-gray-500 text-xs">Verificando automaticamente</span>
              </motion.div>
            )}

            {(externalRefRef.current || searchParams.get('collection_id')) && (
              <p className="text-gray-500 mb-4 text-xs">
                Ref: <span className="font-mono text-gray-400">{externalRefRef.current || searchParams.get('collection_id')}</span>
              </p>
            )}

            {/* WhatsApp como opcion secundaria */}
            <div className="bg-green-900/20 border border-green-500/30 rounded-2xl p-4 mt-4">
              <p className="text-gray-300 text-sm mb-3">
                Si tu pago fue debitado y necesitas ayuda inmediata:
              </p>
              <Button 
                onClick={openWhatsAppSupport}
                className="w-full rounded-full py-4 text-base font-semibold"
                style={{ 
                  background: 'linear-gradient(135deg, #25D366, #128C7E)',
                  boxShadow: '0 4px 15px rgba(37, 211, 102, 0.3)'
                }}
                data-testid="whatsapp-support-btn"
              >
                <FaWhatsapp className="mr-2 text-lg" /> Escribir por WhatsApp
              </Button>
            </div>

            <Button 
              onClick={() => navigate('/')}
              variant="ghost"
              className="w-full rounded-full py-4 text-gray-500 hover:text-white mt-3 text-sm"
            >
              Volver al Inicio
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ========== RENDER: REJECTED (error REAL confirmado por MP) ==========
  if (pageStatus === 'rejected') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0a0a0a' }}>
        <BackgroundEffects />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full relative z-10"
          data-testid="payment-rejected"
        >
          <div 
            className="rounded-3xl p-8 text-center"
            style={{ 
              background: 'linear-gradient(145deg, rgba(50, 50, 50, 0.9), rgba(30, 30, 30, 0.95))',
              border: '1px solid rgba(255, 100, 100, 0.3)',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
            }}
          >
            <div 
              className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ 
                background: 'linear-gradient(135deg, #FF6B6B, #EE5A24)',
                boxShadow: '0 10px 30px rgba(255, 107, 107, 0.3)'
              }}
            >
              <FaTimesCircle className="text-white text-5xl" />
            </div>

            <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif', color: '#FF6B6B' }}>
              PAGO NO APROBADO
            </h1>
            
            <p className="text-gray-300 mb-6 text-lg" data-testid="rejected-message">
              {statusMessage || 'El pago no fue aprobado por MercadoPago.'}
            </p>

            <div className="space-y-3">
              {canRetry && (
                <Button 
                  onClick={() => navigate(-1)}
                  className="w-full rounded-full py-6 text-lg font-bold"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
                  data-testid="retry-payment-btn"
                >
                  Intentar Nuevamente
                </Button>
              )}
              
              <div className="bg-green-900/20 border border-green-500/30 rounded-2xl p-4">
                <p className="text-gray-300 text-sm mb-3">
                  Si crees que hubo un error, escribenos:
                </p>
                <Button 
                  onClick={openWhatsAppSupport}
                  className="w-full rounded-full py-4 text-base font-semibold"
                  style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
                  data-testid="whatsapp-rejected-btn"
                >
                  <FaWhatsapp className="mr-2" /> Escribir por WhatsApp
                </Button>
              </div>
              
              <Button 
                onClick={() => navigate('/')}
                variant="ghost"
                className="w-full rounded-full py-5 text-gray-400 hover:text-white"
              >
                Volver al Inicio
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ========== RENDER: FORMULARIO PERSONALIZADO ==========
  if (showPersonalizedForm && !formCompleted) {
    return (
      <div className="min-h-screen py-12 px-4" style={{ backgroundColor: '#0a0a0a' }}>
        <BackgroundEffects />
        <div className="max-w-3xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
              style={{ background: 'linear-gradient(135deg, #00C853, #00E676)' }}
            >
              <FaCheckCircle className="text-white text-4xl" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Pago Exitoso!</h1>
            <p className="text-gray-400 text-lg">
              Ahora completa el siguiente formulario para que podamos crear tu plan personalizado.
            </p>
          </motion.div>
          
          <PersonalizedPlanForm 
            planType={purchaseData?.plan_type || 'premium'} 
            onComplete={handleFormComplete}
            transactionId={searchParams.get('payment_id') || searchParams.get('id')}
            buyerEmail={purchaseData?.user?.email || purchaseData?.buyer_email || ''}
            buyerName={purchaseData?.user?.name || ''}
          />
        </div>
      </div>
    );
  }

  // ========== RENDER: APPROVED (exito!) ==========
  const productConfig = getProductConfig();
  const IconComponent = productConfig.icon;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 overflow-hidden" style={{ backgroundColor: '#0a0a0a' }} data-testid="payment-approved">
      <BackgroundEffects />
      <AnimatedParticles />

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-xl w-full relative z-10"
      >
        <div 
          className="rounded-3xl backdrop-blur-xl p-8 md:p-12 text-center"
          style={{ 
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02))',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
          >
            <div 
              className="w-28 h-28 rounded-full flex items-center justify-center mx-auto mb-8 relative"
              style={{ background: 'linear-gradient(135deg, #00C853, #00E676)' }}
            >
              <FaCheckCircle className="text-white text-6xl" />
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{ border: '3px solid #00E676' }}
                animate={{ scale: [1, 1.3, 1], opacity: [1, 0, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {isRenewal ? (
              <>
                <h1 
                  className="text-4xl md:text-5xl font-bold mb-4"
                  style={{ 
                    fontFamily: 'Poppins, sans-serif',
                    background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Bienvenida de vuelta!
                </h1>
                <p className="text-xl text-white mb-2">
                  Es un placer continuar <span style={{ color: 'var(--brand-primary)' }}>contigo</span>
                </p>
                <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                  Gracias por seguir confiando en <strong className="text-white">DeisyCFit</strong>. 
                  Tu renovacion ha sido procesada exitosamente.
                  <span className="block mt-2" style={{ color: '#CCFF00' }}>
                    Sigamos trabajando juntas en tus metas!
                  </span>
                </p>
              </>
            ) : (
              <>
                <h1 
                  className="text-4xl md:text-5xl font-bold mb-4"
                  style={{ 
                    fontFamily: 'Poppins, sans-serif',
                    background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Bienvenida!
                </h1>
                <p className="text-xl text-white mb-2">
                  A mi mundo <span style={{ color: 'var(--brand-primary)' }}>Fitness</span>
                </p>
                <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                  Yo soy <strong className="text-white">DeisyCFit</strong> y te estare 
                  acompanando desde el dia de hoy en tu transformacion. 
                  <span className="block mt-2" style={{ color: '#CCFF00' }}>
                    Preparate para alcanzar tus metas!
                  </span>
                </p>
              </>
            )}
          </motion.div>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="h-1 w-32 mx-auto mb-8 rounded-full"
            style={{ background: 'linear-gradient(90deg, var(--brand-primary), #00F0FF, #CCFF00)' }}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="space-y-4"
          >
            {purchaseData?.file_url && (
              <Button 
                onClick={() => window.open(purchaseData.file_url, '_blank')}
                className="w-full text-white rounded-full font-bold text-lg py-6"
                style={{ 
                  background: 'linear-gradient(135deg, #00C853, #00E676)',
                  boxShadow: '0 4px 20px rgba(0, 200, 83, 0.3)'
                }}
              >
                <FaDownload className="mr-3" /> Descargar Ahora
              </Button>
            )}

            {needsForm && !formCompleted ? (
              <Button 
                onClick={() => setShowPersonalizedForm(true)}
                className="w-full text-white rounded-full font-bold text-lg py-6 group animate-pulse"
                style={{ 
                  background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))',
                  boxShadow: '0 4px 30px rgba(var(--brand-primary-glow), 0.5)'
                }}
                data-testid="complete-form-btn"
              >
                <FaClipboardList className="mr-3" /> 
                {isRenewal ? 'Formulario de Actualizacion' : 'Completar mi Formulario'}
                <FaArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" />
              </Button>
            ) : (
              <Button 
                onClick={handleGoToDashboard}
                className="w-full text-white rounded-full font-bold text-lg py-6 group"
                style={{ 
                  background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))',
                  boxShadow: '0 4px 20px rgba(var(--brand-primary-glow), 0.3)'
                }}
                data-testid="go-to-dashboard-btn"
              >
                <IconComponent className="mr-3" /> 
                {productConfig.text}
                <FaArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" />
              </Button>
            )}

            <Button 
              onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hola Deisy! Acabo de comprar mi plan')}`, '_blank')}
              className="w-full rounded-full font-semibold py-5"
              style={{ 
                background: 'transparent',
                border: '2px solid #25D366',
                color: '#25D366'
              }}
            >
              <FaWhatsapp className="mr-2" /> Dudas? Escribeme al WhatsApp
            </Button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="text-gray-500 text-sm mt-8"
          >
            Recibiras un correo con los detalles de tu compra
          </motion.p>
        </div>
      </motion.div>
    </div>
  );
};

const BackgroundEffects = () => (
  <div 
    className="fixed inset-0 z-0 pointer-events-none"
    style={{
      background: 'radial-gradient(circle at 30% 20%, rgba(var(--brand-primary-glow), 0.15), transparent 50%), radial-gradient(circle at 70% 80%, rgba(0, 240, 255, 0.1), transparent 40%)'
    }}
  />
);

const PARTICLE_POSITIONS = [
  { left: '5%', top: '10%', duration: 3.2, delay: 0.1 },
  { left: '15%', top: '25%', duration: 4.1, delay: 0.8 },
  { left: '25%', top: '15%', duration: 3.5, delay: 1.2 },
  { left: '35%', top: '30%', duration: 4.3, delay: 0.3 },
  { left: '45%', top: '5%', duration: 3.8, delay: 1.5 },
  { left: '55%', top: '20%', duration: 4.0, delay: 0.6 },
  { left: '65%', top: '35%', duration: 3.3, delay: 1.8 },
  { left: '75%', top: '10%', duration: 4.5, delay: 0.2 },
  { left: '85%', top: '25%', duration: 3.7, delay: 1.1 },
  { left: '95%', top: '15%', duration: 4.2, delay: 0.9 },
  { left: '10%', top: '60%', duration: 3.4, delay: 1.4 },
  { left: '20%', top: '75%', duration: 4.4, delay: 0.4 },
  { left: '30%', top: '85%', duration: 3.6, delay: 1.7 },
  { left: '40%', top: '70%', duration: 4.1, delay: 0.7 },
  { left: '50%', top: '90%', duration: 3.9, delay: 1.0 },
  { left: '60%', top: '65%', duration: 4.3, delay: 0.5 },
  { left: '70%', top: '80%', duration: 3.1, delay: 1.6 },
  { left: '80%', top: '55%', duration: 4.0, delay: 1.3 },
  { left: '90%', top: '70%', duration: 3.8, delay: 0.0 },
  { left: '50%', top: '50%', duration: 4.5, delay: 1.9 },
];

const AnimatedParticles = () => (
  <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
    {PARTICLE_POSITIONS.map((pos, i) => (
      <motion.div
        key={i}
        className="absolute w-2 h-2 rounded-full"
        style={{
          background: i % 3 === 0 ? 'var(--brand-primary)' : i % 3 === 1 ? '#00F0FF' : '#CCFF00',
          left: pos.left,
          top: pos.top,
        }}
        animate={{
          y: [0, -30, 0],
          opacity: [0.3, 0.8, 0.3],
          scale: [1, 1.5, 1],
        }}
        transition={{
          duration: pos.duration,
          repeat: Infinity,
          delay: pos.delay,
        }}
      />
    ))}
  </div>
);

export default PaymentSuccess;
